/**
 * FuelCostEditor — the company's fuel cost per mile (DEC-326 item 3a, Corbin 2026-10-13).
 *
 * Reads / saves GET / PATCH /v1/tenants/fuel-settings (MANAGE_TENANT). Fuel cost per load
 * = the load's miles x this rate. A new rate applies to loads measured from now on; past
 * loads keep the rate they were measured with. The page says so.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useApiOpts, authedFetch } from '@lodex-pro/web-shared';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';

import { fuelRateToInput, parseFuelRate } from './fuel-rate';

interface FuelSettings {
  readonly fuelCostCentsPerMile: number | null;
}

const help: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

export function FuelCostEditor(): React.JSX.Element {
  const opts = useApiOpts();
  const [saved, setSaved] = React.useState<FuelSettings | null>(null);
  const [text, setText] = React.useState('');
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(async (): Promise<void> => {
    try {
      const res = await authedFetch(opts, '/v1/tenants/fuel-settings');
      if (!res.ok) {
        setLoadError(`Could not load the fuel rate (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: FuelSettings };
      setSaved(json.settings);
      setText(fuelRateToInput(json.settings.fuelCostCentsPerMile));
      setLoadError(null);
    } catch {
      setLoadError('Network error — please try again.');
    }
  }, [opts]);

  React.useEffect(() => {
    void load();
  }, [load]);

  if (loadError !== null) {
    return (
      <div style={{ color: 'var(--color-danger)' }}>
        <p style={{ marginBottom: '8px' }}>{loadError}</p>
        <Button variant="outline" onClick={(): void => void load()}>
          Retry
        </Button>
      </div>
    );
  }
  if (saved === null) {
    return (
      <p role="status" style={help}>
        Loading…
      </p>
    );
  }

  const parsed = parseFuelRate(text);
  const unchanged = parsed.ok && parsed.cents === saved.fuelCostCentsPerMile;

  const save = async (): Promise<void> => {
    if (!parsed.ok) return;
    setSaving(true);
    try {
      const res = await authedFetch(opts, '/v1/tenants/fuel-settings', {
        method: 'PATCH',
        body: JSON.stringify({ fuelCostCentsPerMile: parsed.cents }),
      });
      if (!res.ok) {
        toast.error(`Save failed (${String(res.status)}).`);
        return;
      }
      const json = (await res.json()) as { settings: FuelSettings };
      setSaved(json.settings);
      setText(fuelRateToInput(json.settings.fuelCostCentsPerMile));
      toast.success('Fuel rate saved');
    } catch {
      toast.error('Network error — please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: '520px' }}>
      <label htmlFor="fuel-rate" style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>
        Fuel cost per mile ($)
      </label>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <Input
          id="fuel-rate"
          inputMode="decimal"
          placeholder="Not set"
          value={text}
          aria-invalid={!parsed.ok}
          aria-describedby="fuel-rate-help"
          onChange={(e): void => setText(e.target.value)}
          style={{ maxWidth: '160px' }}
        />
        <Button
          type="button"
          disabled={!parsed.ok || unchanged || saving}
          onClick={(): void => void save()}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      <p id="fuel-rate-help" style={{ ...help, marginTop: '6px' }}>
        {parsed.ok
          ? 'Fuel cost for a load = its miles × this rate. Leave blank to show miles only. A new rate applies to loads measured from now on.'
          : parsed.message}
      </p>
      <p style={{ ...help, marginTop: '10px' }}>
        Miles come from the driver&apos;s GPS trail when it covers the drive. Otherwise they are
        estimated as the straight line from pickup to disposal × 1.3, and shown as estimates.
      </p>
    </div>
  );
}
